// Chrome fires `beforeinstallprompt` once, early, and only a page that kept
// the event can open the install dialog later. It is captured here at startup
// so both the phone banner and the /install page can use it, whichever is
// showing when the owner taps Install.
//
// Subscribers get one state, { canPrompt, installed }, so an install made
// from the browser's own menu reaches every screen, not just the button that
// happened to start it.
let deferredPrompt = null;
let installedNow = false;
let captureStarted = false;
const listeners = new Set();
const INSTALLED_KEY = 'serviti-pwa-installed';

export const wasInstalledBefore = () => {
  try { return localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; }
};

const rememberInstalled = () => {
  installedNow = true;
  try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* Storage is optional. */ }
};

export const isInstalled = () => {
  if (installedNow) return true;
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone,
  );
};

export const getInstallState = () => ({ canPrompt: Boolean(deferredPrompt), installed: isInstalled() });

const notify = () => {
  const state = getInstallState();
  listeners.forEach((listener) => listener(state));
};

export const installPwaCapture = () => {
  if (typeof window === 'undefined' || captureStarted) return;
  captureStarted = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    // A fresh browser offer is stronger evidence than a remembered install
    // (the person may have uninstalled it since their last visit).
    installedNow = false;
    try { localStorage.removeItem(INSTALLED_KEY); } catch { /* Storage is optional. */ }
    deferredPrompt = event;
    notify();
  });
  // Fires however it was installed: our button, the browser menu or the
  // address-bar icon.
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    rememberInstalled();
    notify();
  });
};

export const subscribeInstallPrompt = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Opens Chrome's install dialog. Returns 'accepted', 'dismissed' or
// 'unavailable'. An event can be prompted only once, so it is spent before
// prompting: after a dismissal the screens fall back to the manual steps
// instead of offering a button that can no longer work. Chrome sends a fresh
// event if it will allow another attempt.
export const promptInstall = async () => {
  const event = deferredPrompt;
  if (!event) return 'unavailable';
  deferredPrompt = null;
  notify();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === 'accepted') {
      rememberInstalled();
      notify();
    }
    return outcome;
  } catch {
    return 'unavailable';
  }
};

// Which instructions to show. iPadOS reports itself as a Mac, so a Mac with
// a touch screen is an iPad.
export const devicePlatform = (nav = typeof navigator === 'undefined' ? {} : navigator) => {
  const ua = nav.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && (nav.maxTouchPoints || 0) > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

// Tests only: module state outlives a single test.
export const resetInstallStateForTests = () => {
  deferredPrompt = null;
  installedNow = false;
  notify();
};
